import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { EMPTY } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, mergeMap, share, switchMap, tap } from 'rxjs/operators';
import isEqual from 'lodash-es/isEqual';

import { RpcRoute } from '@core-layout/app/models/rpc-route';
import { RouteService } from '@core-layout/app/services/route.service';
import { ToastService } from '@core-services/toast.service';
import { LISTING_ERRORS_MAP } from '@listings/constants/listing-errors.constants';
import * as listingActions from '@listings/store/actions/listings.actions';
import { ListingApiService } from '@listings/store/services/listing-api.service';
import * as settingsActions from '@settings/store/actions/settings.actions';
import * as listingActivityActions from '../actions/listing-activity.actions';
import { ListingsStoreService } from '../services/listings-store.service';

@Injectable()
export class ListingsEffects {
    private readonly customerListingsDebounceTime = 200;
    private readonly marketListingsDebounceTime = 500;

    constructor(
        private readonly actions$: Actions,
        private readonly listingApiService: ListingApiService,
        private readonly listingsStoreService: ListingsStoreService,
        private readonly toaster: ToastService,
        private readonly routeService: RouteService
    ) { }

    public readonly loadListing$ = createEffect(() =>
        this.actions$.pipe(
            ofType(listingActions.loadListing),
            debounceTime(this.customerListingsDebounceTime),
            switchMap((params: { id: string }) => this.listingApiService.loadListing(params.id)),
            share()
        )
    );

    public readonly loadListinsgByHashCodes$ = createEffect(() => this.actions$.pipe(
        ofType(listingActions.loadListinsgByHashCodes),
        mergeMap(({ hashCodes }) => this.listingApiService.loadListingsByHashCodes(hashCodes))
    ));

    public readonly loadCustomerListings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(listingActions.loadCustomerListings),
            debounceTime(this.customerListingsDebounceTime),
            switchMap(() => this.listingApiService.loadCustomerListings()),
            share()
        )
    );

    public readonly loadListingsMedia$ = createEffect(() =>
        this.actions$.pipe(
            ofType(listingActions.loadListingsMedia),
            concatLatestFrom(() => this.listingsStoreService.getAllListingsMedia()),
            distinctUntilChanged(isEqual),
            mergeMap(([action, medias]) => {
                const mediaToFetch = action.listingIds.filter(o => medias[o] === undefined);

                if (mediaToFetch.length === 0) {
                    return EMPTY;
                }

                return this.listingApiService.loadListingsMedia(mediaToFetch);
            }),
        )
    );

    public readonly loadMarketListings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(listingActions.loadMarketListings),
            debounceTime(this.marketListingsDebounceTime),
            switchMap(({ searchOptions }) => this.listingApiService.loadMarketListings(searchOptions)),
            share()
        )
    );

    public readonly markAsViewed$ = createEffect(() =>
        this.actions$.pipe(
            ofType(listingActions.markAsViewed),
            switchMap((params: { listingId: string, listingHashCode: number }) =>
                this.listingApiService.markAsViewed(params.listingId, params.listingHashCode)),
            share()
        )
    );

    public readonly loadListingsByIds$ = createEffect(() => this.actions$.pipe(
        ofType(listingActions.reloadListingsByIds),
        switchMap(({ listingsToLoadIds, listingsToDeleteIds }) =>
            this.listingApiService.reloadListingsByIds(listingsToLoadIds, listingsToDeleteIds))
    ));

    public readonly showListingError$ = createEffect(
        () => this.actions$.pipe(
            ofType(
                listingActions.softDeleteFailed,
                listingActions.hardDeleteFailed,
                listingActivityActions.setListingsActivityFailed,
                listingActions.restoreFailed
            ),
            tap(({ error }) => {
                const localizationKey = LISTING_ERRORS_MAP.get(error.errorKey);

                if (localizationKey != null) {
                    this.toaster.showClientError(localizationKey);
                }
            })
        ),
        { dispatch: false }
    );

    public readonly loadMarketListingsFailed$ = createEffect(() => this.actions$.pipe(
        ofType(listingActions.loadMarketListingsFailed),
        map(() => {
            this.routeService.navigate(RpcRoute.Portfolio);
            return settingsActions.loadSettings();
        }
        )
    ));
}
